import { Either, Schema } from "effect";
import superjson from "superjson";
import { kitchenNotesRecordSchema } from "./kitchen-notes.ts";

/** Provider fields needed to identify a kitchen line without using its array position. */
export const kitchenLineSchema = Schema.Struct({
  uid: Schema.optional(Schema.String),
  catalogObjectId: Schema.optional(Schema.String),
  name: Schema.optional(Schema.String),
  kitchenName: Schema.optional(Schema.String),
  variationName: Schema.optional(Schema.String),
  quantity: Schema.transform(
    Schema.Union(Schema.String, Schema.Number),
    Schema.String,
    { decode: String, encode: String },
  ),
  note: Schema.optional(Schema.String),
  modifiers: Schema.optional(
    Schema.Array(
      Schema.Struct({
        uid: Schema.optional(Schema.String),
        catalogObjectId: Schema.optional(Schema.String),
        name: Schema.optional(Schema.String),
        quantity: Schema.optional(Schema.String),
      }),
    ),
  ),
});
/** A parsed provider line, shared by display and server-side instruction targeting. */
export type KitchenLine = typeof kitchenLineSchema.Type;

/** Item label used for accessible controls and snapshots of subsequently removed items. */
export const kitchenLineLabel = (line: KitchenLine) =>
  line.kitchenName || line.name || "Item";

/**
 * Prefer Square's stable line UID. Legacy rows use a collision-free serialized
 * identity, not an array index or lossy hash. Ambiguous identical rows are not editable.
 * Legacy identity deliberately changes if its quantity/details change.
 */
export function kitchenLineKey(line: KitchenLine): string {
  return line.uid
    ? `square:${line.uid}`
    : `legacy:${JSON.stringify([
        line.catalogObjectId ?? null,
        line.name ?? null,
        line.kitchenName ?? null,
        line.variationName ?? null,
        line.quantity,
        line.note ?? null,
        line.modifiers?.map((modifier) => [
          modifier.uid ?? null,
          modifier.catalogObjectId ?? null,
          modifier.name ?? null,
          modifier.quantity ?? null,
        ]) ?? [],
      ])}`;
}

/** Counted tags represent whole ordered units; fractional/invalid quantities allow text only. */
export function kitchenLineLimit(line: KitchenLine): number {
  const quantity = Number(line.quantity);
  return Number.isSafeInteger(quantity) && quantity > 0
    ? Math.min(99, quantity)
    : 0;
}

const lineKeySchema = Schema.String.pipe(
  Schema.minLength(1),
  Schema.maxLength(4000),
);
/** One requested line edit; callers cannot replace the whole stored list or invent its label. */
export const putLineKitchenNoteSchema = Schema.Struct({
  ...kitchenNotesRecordSchema.fields,
  line_key: lineKeySchema,
});
/** Stored per-line snapshots remain distinct from provider notes and order-wide information. */
export const lineKitchenNoteSchema = Schema.Struct({
  ...putLineKitchenNoteSchema.fields,
  line_label: Schema.String.pipe(Schema.minLength(1)),
});
/** Parsed stored per-line instructions. */
export type LineKitchenNote = typeof lineKitchenNoteSchema.Type;
/** Bounded, unique line associations on the canonical Order record. */
export const lineKitchenNotesSchema = Schema.Array(lineKitchenNoteSchema).pipe(
  Schema.maxItems(100),
  Schema.filter(
    (notes) =>
      new Set(notes.map((note) => note.line_key)).size === notes.length ||
      "Each item must have only one instruction record.",
  ),
);
/** Typed failure without raw provider details or customer/staff text. */
export class InvalidLineInstructions extends Error {
  /** Safe domain error discriminator. */
  readonly _tag = "InvalidLineInstructions" as const;
  /** Explain why a selected item cannot receive these counts. */
  constructor() {
    super(
      "Select a current, uniquely identifiable item and keep instruction counts within its ordered quantity.",
    );
  }
}

/** Find only an unambiguous current line; never guess among duplicate or removed items. */
export function findKitchenLine(
  lines: readonly KitchenLine[],
  key: string,
): KitchenLine | undefined {
  const matches = lines.filter((line) => kitchenLineKey(line) === key);
  return matches.length === 1 &&
    Either.isRight(Schema.decodeUnknownEither(lineKeySchema)(key))
    ? matches[0]
    : undefined;
}

/** Replace only one line's instructions, preserving every other line and clearing empty entries. */
export function changeLineInstructions(
  lines: readonly KitchenLine[],
  existing: readonly LineKitchenNote[],
  change: typeof putLineKitchenNoteSchema.Type,
):
  | { readonly ok: true; readonly value: LineKitchenNote[] }
  | { readonly ok: false; readonly error: InvalidLineInstructions } {
  const line = findKitchenLine(lines, change.line_key);
  if (!line || change.tags.some((tag) => tag.count > kitchenLineLimit(line)))
    return { ok: false, error: new InvalidLineInstructions() };
  const others = existing.filter((note) => note.line_key !== change.line_key);
  const value =
    change.tags.length || change.text
      ? [...others, { ...change, line_label: kitchenLineLabel(line) }]
      : others;
  if (value.length > 100)
    return { ok: false, error: new InvalidLineInstructions() };
  return { ok: true, value };
}

/** Parse the saved provider envelope at the persistence boundary, failing closed on unreadable data. */
export function readKitchenLines(
  serialized: string | undefined,
): readonly KitchenLine[] | undefined {
  try {
    const raw: unknown = serialized ? superjson.parse(serialized) : {};
    const result = Schema.decodeUnknownEither(
      Schema.Struct({
        lineItems: Schema.optional(Schema.Array(kitchenLineSchema)),
        returns: Schema.optional(
          Schema.Array(
            Schema.Struct({
              returnLineItems: Schema.optional(Schema.Array(kitchenLineSchema)),
            }),
          ),
        ),
      }),
    )(raw);
    if (Either.isLeft(result)) return undefined;
    return result.right.lineItems?.length
      ? result.right.lineItems
      : (result.right.returns?.flatMap(
          (entry) => entry.returnLineItems ?? [],
        ) ??
          []);
  } catch {
    return undefined;
  }
}
